// useCellArrowNavigation
//
// GH #555: Excel-like Arrow / Shift+Arrow support for the planogram editor,
// shared by PlanogramVisual and PlanogramTabular the same way useCellClipboard
// (Ctrl+C/X/V/Delete) already is. This hook owns ONLY the keydown listener
// and the anchor-tracking needed for Shift+Arrow range math — it has no
// opinion on what "next cell" means geometrically (that's the caller's
// `getNextKey`, backed by PlanogramNavigation's nextVisualCellKey /
// nextTabularCellKey) and no opinion on what a "selection" looks like (that's
// PlanogramSelection / usePlanogramSelection, reused as-is rather than
// duplicated — Shift+Arrow extends the exact same selection-mode concept the
// toolbar's drag-to-select and "Select" button already use, per design).
//
// Cold start (no activeCellKey yet):
//   - *** NEW (explicit request): the FIRST arrow press, pressed while
//     NOTHING is active yet, immediately jumps to the first cell (the
//     caller's `getFirstKey()`) — the same "prove the keyboard shortcut is
//     alive" affordance useGridEnterNavigation's Enter key already has for
//     an unfocused grid. Without this, pressing an arrow key with nothing
//     focused was a silent no-op (see `if (!opts.activeCellKey) return;`
//     previously) — there was no `from` cell to step from, so nothing
//     visibly happened, which looked exactly like "arrow keys don't work"
//     even though the keyboard listener was firing correctly. Plain arrow
//     and Shift+Arrow are both covered: a cold Shift+Arrow press now
//     anchors AND focuses the first cell in one step (a 1-cell "range"),
//     exactly mirroring a cold plain-arrow press landing on that same
//     cell as the new activeCellKey.
//   - This is a single first-cell JUMP, not a real "move" — the direction
//     key itself is not used to compute it (there is no `from` yet), it
//     only marks the END of the very first press as "now something is
//     active." Every subsequent press behaves exactly as before, stepping
//     from that new activeCellKey via getNextKey.
//
// Plain Arrow (no Shift):
//   - Moves activeCellKey to whatever getNextKey returns for that direction.
//   - If a Shift+Arrow range selection is currently active, collapses it
//     back down to a single cell (the new activeCellKey) and exits
//     selection mode — same as Excel: an unmodified arrow always replaces
//     a range with a single-cell selection, it never just "stops extending".
//   - No-ops (but still prevents default) at the grid's edge, where
//     getNextKey returns null — mirrors useGridEnterNavigation's own
//     "stop at the edge, don't wrap" behavior for Tab/Enter.
//
// Shift+Arrow:
//   - First press from a cold (non-selection-mode) state: the CURRENT
//     activeCellKey becomes the fixed anchor, and the single adjacent cell
//     in that direction becomes the initial 2-cell range. This mirrors
//     onSelectionDragCommit's existing "a 2+ cell drag from a cold state
//     auto-enters selection mode" contract, just driven by the keyboard
//     instead of the mouse.
//   - Every subsequent press (still holding Shift, same or other
//     direction) moves the FOCUS end only; the anchor never moves. The
//     selection is recomputed from scratch as the full anchor->focus
//     rectangle each time (via `computeRange`), so Shift+Down, Shift+Down,
//     Shift+Up correctly shrinks back by one row rather than only ever
//     growing — see usePlanogramSelection's `replace` for why setMany alone
//     can't do this.
//   - activeCellKey itself does NOT move while shift-extending (matches
//     Excel: the active/edited cell stays the anchor; only the highighted
//     range grows). It's restored to the anchor if the range is later
//     collapsed by a plain arrow.
//
// *** FIX (autocomplete arrow-key collision): Arrow Up/Down inside an open
// PlanogramCell/PlanogramTabular Product AutoComplete suggestion panel
// (ResourceAutoComplete, backed by PrimeReact's AutoComplete) used to
// collide with this hook's own grid navigation. ***
//
// While the product suggestion panel is open, ArrowUp/ArrowDown are meant
// to move the highlighted suggestion — that's PrimeReact's own internal
// keydown handling, and it's the entire point of being able to arrow
// through "test / 100 ML", "test / 1210 L", etc. before pressing Enter to
// confirm one. shouldYieldToTextCaret (below) previously only ever yielded
// for Left/Right (plain text-caret movement) and NEVER for Up/Down, on the
// reasoning that a single-line input's caret has no vertical-movement
// meaning. That reasoning is correct for an ordinary input, but wrong the
// moment an autocomplete dropdown is actually open: Up/Down very much has
// a job to do there. Because this hook's listener is bound at the window
// level (so it can also catch a "cold" arrow press anywhere on the page —
// see the cold-start handling above), it fired on the SAME keydown as
// PrimeReact's own handler — the suggestion list visibly moved AND
// activeCellKey jumped to the next grid cell/row at the same time, with
// preventDefault() racing the two outcomes.
//
// useGridEnterNavigation already solved this identical problem for Enter
// via a ".p-autocomplete-panel" visibility check; hasOpenAutocompletePanel
// below mirrors that, and is consulted first thing in shouldYieldToTextCaret
// so that while any suggestion panel is open, this hook's listener takes no
// action at all (no preventDefault, no setActiveCellKey, no Shift+Arrow
// range math) on ANY arrow direction — PrimeReact gets the key entirely to
// itself, the same way it already does for Enter. This is checked
// independently of event.target, since the open panel itself is rendered
// at the document body level by PrimeReact, not as a descendant of the
// focused input.
//
// *** FIX (GH #577): the background grid still reacted to Arrow Up/Down
// while AddEditCellSettingsDialog (the "assign product to selected cells"
// modal) was open and its own product dropdown was expanded. ***
//
// hasOpenAutocompletePanel() above only ever checks for the literal
// `.p-autocomplete-panel` class — it protects every case where the open
// dropdown IS a PrimeReact AutoComplete rendered the same way
// ResourceAutoComplete renders it elsewhere in the grid. But it gives no
// protection at all if the modal's dropdown happens to be a different
// component, is portaled differently, or the selector simply doesn't
// match for any other reason — in that situation hasOpenAutocompletePanel()
// returns false even though a dropdown the person can clearly see IS
// open, and shouldYieldToTextCaret falls through into ordinary
// grid-navigation logic, which is exactly the reported symptom: arrow
// keys moved the BACKGROUND cell selection/activeCellKey instead of
// navigating the modal's own list.
//
// The actual invariant this hook needs to respect is broader than "is an
// autocomplete panel open" — it's "is the planogram grid currently the
// thing the person is interacting with at all." Once a modal dialog is
// open, the grid is background UI by definition (the dialog has its own
// focus trap, or should), so EVERY keyboard shortcut this hook owns must
// stay out of the way entirely, regardless of what's open inside that
// dialog (a dropdown, a plain input, nothing yet). hasOpenModalDialog
// below checks for PrimeReact's own Dialog overlay class (`.p-dialog-mask`,
// present while any PrimeReact Dialog — which AddEditCellSettingsDialog
// and every other modal in this codebase is built on — is mounted) and
// the generic ARIA `role="dialog"` as a framework-agnostic fallback.
// Checked alongside hasOpenAutocompletePanel, not instead of it, since
// the autocomplete check still matters for the in-grid case (a product
// field focused directly inside a `.grid-cell`/table row, with no dialog
// involved at all) where there is no modal to detect.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef } from 'react';
import { ArrowDirection } from '../domain/PlanogramNavigation';
import { CellCoordinates, CellKey, parseCellKey } from '../domain/types';

export interface UseCellArrowNavigationOptions {
    /** Currently-active single cell, or null if nothing is focused yet — same activeCellKey concept useCellClipboard anchors on. */
    activeCellKey: CellKey | null;
    setActiveCellKey: (key: CellKey | null) => void;
    /**
     * Given the current cell and a direction, returns the next cell's key,
     * or null if there is nowhere to go (grid edge, or activeCellKey isn't
     * set). Visual passes nextVisualCellKey, Tabular passes
     * nextTabularCellKey — see PlanogramNavigation.ts.
     */
    getNextKey: (from: CellCoordinates, direction: ArrowDirection) => CellKey | null;
    /**
     * *** NEW: returns the key of the first cell (top-left in Visual,
     * first row in Tabular), or null if there's nothing to navigate into
     * yet (e.g. an empty grid). Called ONLY on a cold arrow press — when
     * activeCellKey is null — to jump straight there, the same "first
     * press proves it's alive" affordance Enter already has via
     * useGridEnterNavigation. Optional: a caller that omits this keeps
     * the previous behavior of doing nothing on a cold arrow press,
     * since there is no document-order DOM concept this hook can fall
     * back on by itself — only the caller knows what "first cell" means
     * for its own layout.
     */
    getFirstKey?: () => CellKey | null;
    /**
     * Computes the full set of keys the selection should contain for a
     * given anchor->focus pair (a rectangle in Visual; same rectangle
     * math reused for Tabular even though it renders as a flat list — see
     * PlanogramSelection.rectangleKeys).
     */
    computeRange: (anchor: CellCoordinates, focus: CellCoordinates) => CellKey[];
    isSelectionMode: boolean;
    /** Enters selection mode with an initial set of keys already selected — see usePlanogramSelection.enterSelectionModeWith. */
    enterSelectionModeWith: (keys: CellKey[]) => void;
    /** Replaces the selection's contents outright — see usePlanogramSelection.replace. */
    replaceSelection: (keys: CellKey[]) => void;
    exitSelectionMode: () => void;
    /** Disables the whole listener — read-only mode, or the grid not showing cells yet. */
    disabled?: boolean;
}

const isTextInputElement = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement =>
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

const isContentEditableElement = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement && target.isContentEditable;

/**
 * True if a PrimeReact AutoComplete suggestion panel is currently open and
 * visible anywhere on the page. The panel is rendered via a portal to
 * document.body (same as PrimeReact's Dialog/Sidebar/Dropdown overlays —
 * see useGridEnterNavigation's identical "foreign overlay" handling for
 * Enter), so this deliberately does NOT check event.target/containment —
 * the open panel is never a DOM descendant of the focused input it belongs
 * to, only its sibling.
 *
 * Mirrors useGridEnterNavigation's `hasOpenAutocompletePanel` check
 * one-for-one. If this selector ever needs to change (e.g. a PrimeReact
 * version bump renders the panel differently), keep both in sync.
 */
const hasOpenAutocompletePanel = (): boolean =>
    !!document.querySelector('.p-autocomplete-panel:not([style*="display: none"])');

/**
 * *** NEW (GH #577): true while ANY modal dialog is open anywhere on the
 * page, regardless of what's inside it. ***
 *
 * `.p-dialog-mask` is PrimeReact's own Dialog overlay/backdrop element —
 * present in the DOM for the full lifetime of any open PrimeReact
 * `<Dialog>`, which every modal in this codebase (AddEditCellSettingsDialog
 * included) is built on, independent of whatever content that particular
 * dialog happens to render inside itself. `[role="dialog"]` is also
 * checked as a framework-agnostic fallback for any modal that isn't a
 * PrimeReact Dialog. This is intentionally broader than
 * hasOpenAutocompletePanel — see this file's GH #577 header comment for
 * why "is a dialog open at all" is the correct invariant here, not "is
 * the specific dropdown class we expect open."
 */
const hasOpenModalDialog = (): boolean =>
    !!document.querySelector('.p-dialog-mask, [role="dialog"]');

/**
 * *** FIX (GH #555): Arrow-key cell navigation did nothing at all in
 * PlanogramTabular. ***
 *
 * The bug: this hook's keydown listener bailed unconditionally whenever
 * event.target was an INPUT/TEXTAREA/contentEditable element — a blanket
 * "don't steal keys while the person is typing" guard. That's the right
 * instinct for PlanogramVisual, where the active cell is a plain
 * focusable <div> (cell editing happens in a separate dialog) and a text
 * input is the exception, not the rule. But PlanogramTabular renders
 * every editable field (Product/EAN/Weight/Price/Min/Max — see
 * PlanogramTabular's *Body renderers) as a real PrimeReact InputText,
 * i.e. an <input>, and that's exactly where activeCellKey's focus lives
 * during ordinary use (see handleFieldFocus's onFocus wiring). So in
 * Tabular, the guard fired on essentially every keypress, every time —
 * arrow keys, Shift+Arrow, all of it — and the listener below never ran.
 * That's the reported "navigation/selection isn't working at all in
 * tabular view" symptom; it was never reaching this code.
 *
 * The fix can't just delete the guard — Left/Right inside a real text
 * field legitimately needs to move the text caret, not jump cells, while
 * there's still text to move across. So this replaces the blanket check
 * with direction-aware logic:
 *   - Left/Right: still yields to native caret movement WHILE there's
 *     text on the relevant side of the caret to move across (mirrors
 *     Excel: typing in a cell, Left/Right moves within the text until
 *     you hit the start/end, then the next press moves to the next
 *     cell). If the caret is already at that edge (or there's no
 *     selection-range API, e.g. some input types), the press falls
 *     through to cell navigation instead of being silently swallowed.
 *   - Up/Down: ALWAYS falls through to cell navigation. A single-line
 *     <input>/<textarea> caret has no vertical-movement meaning, so
 *     there's nothing for Up/Down to do inside the field — unlike
 *     Left/Right, suppressing these has no native behavior to protect,
 *     only a bug to cause.
 *
 *     *** EXCEPT when an autocomplete suggestion panel is open, OR a
 *     modal dialog is open — see hasOpenAutocompletePanel and
 *     hasOpenModalDialog above, both checked at the very top of this
 *     function. In either case Up/Down belongs to whatever's active
 *     inside that panel/dialog, so this is no longer an unconditional
 *     "always falls through" — it only falls through when neither is
 *     open. ***
 *   - contentEditable elements (no selectionStart/selectionEnd API) keep
 *     the original conservative behavior: always yield, on every
 *     direction, since there's no reliable way to tell here whether the
 *     caret is at a text edge.
 */
const shouldYieldToTextCaret = (target: EventTarget | null, direction: ArrowDirection): boolean => {
    // Checked first, independent of target/direction: while a suggestion
    // panel is open, every arrow key belongs to PrimeReact's own list
    // navigation, full stop. See the header comment block above for the
    // full collision this prevents.
    if (hasOpenAutocompletePanel()) return true;

    // *** GH #577: checked just as early, and just as unconditionally —
    // once ANY modal dialog is open, this hook must treat the background
    // grid as out of scope for every arrow press, not just the ones that
    // happen to land on a recognized autocomplete panel. See this file's
    // GH #577 header comment for the full rationale.
    if (hasOpenModalDialog()) return true;

    if (isContentEditableElement(target)) return true;
    if (!isTextInputElement(target)) return false;
    if (direction !== 'left' && direction !== 'right') return false;

    const { selectionStart, selectionEnd, value } = target;
    // Some <input> types (e.g. number, email) don't expose selection
    // offsets at all — selectionStart/End are null. With no way to know
    // where the caret is, be conservative and yield, same as before.
    if (selectionStart === null || selectionEnd === null) return true;

    // A non-collapsed selection: Left/Right is about to collapse it to
    // one edge (standard browser behavior), not move a single caret —
    // always yield so that happens.
    if (selectionStart !== selectionEnd) return true;

    return direction === 'left' ? selectionStart > 0 : selectionEnd < value.length;
};

const ARROW_DIRECTIONS: Record<string, ArrowDirection> = {
    ArrowUp: 'up',
    ArrowDown: 'down',
    ArrowLeft: 'left',
    ArrowRight: 'right',
};

export function useCellArrowNavigation({
    activeCellKey,
    setActiveCellKey,
    getNextKey,
    getFirstKey,
    computeRange,
    isSelectionMode,
    enterSelectionModeWith,
    replaceSelection,
    exitSelectionMode,
    disabled = false,
}: UseCellArrowNavigationOptions) {
    const optionsRef = useRef({
        activeCellKey,
        setActiveCellKey,
        getNextKey,
        getFirstKey,
        computeRange,
        isSelectionMode,
        enterSelectionModeWith,
        replaceSelection,
        exitSelectionMode,
        disabled,
    });
    optionsRef.current = {
        activeCellKey,
        setActiveCellKey,
        getNextKey,
        getFirstKey,
        computeRange,
        isSelectionMode,
        enterSelectionModeWith,
        replaceSelection,
        exitSelectionMode,
        disabled,
    };

    // The fixed anchor for an in-progress Shift+Arrow range — set the
    // moment the first Shift+Arrow press starts a new range, cleared the
    // moment a plain Arrow press (or anything else that ends the range)
    // happens. This is intentionally a ref, not state: it must be readable
    // synchronously inside the same keydown handler that might also set it
    // (the first Shift+Arrow press both establishes AND immediately uses
    // the anchor), and it has no rendering implications of its own.
    const shiftAnchorRef = useRef<CellCoordinates | null>(null);

    // *** FIX (GH #555): Shift+Arrow only ever selected the single cell
    // next to the anchor, no matter how many times it was pressed. ***
    //
    // The bug: every Shift+Arrow press computed `fromCoords` from
    // `opts.activeCellKey` (below) and asked getNextKey for ONE step from
    // there — but activeCellKey is the anchor, and the anchor deliberately
    // never moves while shift-extending (see header comment, and the
    // "activeCellKey deliberately does NOT move" line at the end of the
    // shift branch). So every single press — first, second, tenth, in any
    // direction — re-asked "what's one step from the anchor?" and got the
    // same answer every time. The range could never grow past a 2-cell
    // anchor+neighbor pair; that's exactly the reported "only the closest
    // cell to the active cell gets selected" symptom.
    //
    // The fix needs a THIRD piece of state, distinct from both the anchor
    // (shiftAnchorRef, never moves) and activeCellKey (also never moves
    // while shift-extending): where the FOCUS end of the range currently
    // is. Each subsequent Shift+Arrow press must step getNextKey from the
    // current focus, not from the anchor — that's how the focus walks
    // further away each press, exactly like Excel's Shift+Arrow.
    //
    // Same ref-not-state reasoning as shiftAnchorRef: read synchronously
    // inside the same keydown handler that updates it, no rendering
    // implications on its own.
    const shiftFocusRef = useRef<CellCoordinates | null>(null);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        const opts = optionsRef.current;
        if (opts.disabled) return;

        const direction = ARROW_DIRECTIONS[event.key];
        if (!direction) return;
        if (shouldYieldToTextCaret(event.target, direction)) return;

        // *** NEW: cold start — nothing active yet. Jump straight to the
        // first cell, the same "prove the shortcut is alive" affordance
        // Enter already has via useGridEnterNavigation, instead of
        // silently doing nothing (there's no `from` cell to step from on
        // a genuinely cold press, so the old code just bailed here). This
        // covers BOTH plain Arrow and Shift+Arrow — a cold Shift+Arrow
        // press jumps to the first cell too, landing as a single-cell
        // "range" (anchor === focus === first cell) rather than trying to
        // extend a selection that doesn't exist yet. The direction key
        // itself is irrelevant for this jump (there's nothing to move
        // FROM), so the same first cell is used no matter which arrow was
        // pressed — exactly like Enter doesn't care which row you'd have
        // tabbed to next, it just lands on the first focusable field.
        if (!opts.activeCellKey) {
            const firstKey = opts.getFirstKey?.();
            if (!firstKey) return;
            event.preventDefault();

            if (opts.isSelectionMode) {
                // Already mid-selection somehow with no active cell (an
                // edge case, not the common path) — land on the first
                // cell as a fresh single-cell selection rather than
                // trying to extend a now-meaningless old range.
                opts.exitSelectionMode();
            }
            opts.setActiveCellKey(firstKey);
            return;
        }

        const fromKey = opts.activeCellKey;
        const fromCoords = parseCellKey(fromKey);

        if (event.shiftKey) {
            // Establish the anchor AND the initial focus on the FIRST
            // Shift+Arrow press of a new range. If a range is already in
            // progress, the anchor stays exactly where it was — only the
            // focus end moves, per the hook's own header comment.
            if (!shiftAnchorRef.current) {
                shiftAnchorRef.current = fromCoords;
                // Cold start: the focus end hasn't moved away from the
                // anchor yet either, so step from the anchor itself for
                // this first press only. Every later press in this same
                // range steps from shiftFocusRef instead (see below).
                shiftFocusRef.current = fromCoords;
            }
            const anchor = shiftAnchorRef.current;
            // The cell to step FROM is wherever the focus end currently
            // sits — NOT activeCellKey/fromCoords, which is pinned to the
            // anchor for the whole range per design. This is the crux of
            // the fix: each press extends from the range's current edge,
            // so repeated presses walk progressively further rather than
            // all recomputing the same single step from the anchor.
            const stepFrom = shiftFocusRef.current ?? anchor;

            const nextKey = opts.getNextKey(stepFrom, direction);
            // At the edge of the grid: nothing further to extend into.
            // Still swallow the keypress (matches plain-arrow's own
            // edge behavior below) rather than letting it scroll the page.
            event.preventDefault();
            if (!nextKey) return;

            const focus = parseCellKey(nextKey);
            shiftFocusRef.current = focus;
            const rangeKeys = opts.computeRange(anchor, focus);

            if (opts.isSelectionMode) {
                opts.replaceSelection(rangeKeys);
            } else {
                opts.enterSelectionModeWith(rangeKeys);
            }

            // Keep the visible active border on the moving focus end of the
            // Shift+Arrow range. The fixed anchor still lives in shiftAnchorRef;
            // activeCellKey is only the visual/current focus marker.
            opts.setActiveCellKey(nextKey);

            return;
        }

        // Plain arrow: any in-progress shift-range is done.
        shiftAnchorRef.current = null;
        shiftFocusRef.current = null;

        const nextKey = opts.getNextKey(fromCoords, direction);
        event.preventDefault();
        if (!nextKey) return;

        if (opts.isSelectionMode) {
            // Collapse the range back to a single cell, same as Excel —
            // an unmodified arrow always replaces a multi-cell selection,
            // it never just "stops extending" and leaves the range as-is.
            opts.exitSelectionMode();
        }
        opts.setActiveCellKey(nextKey);
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // If the selection is cleared/exited from somewhere ELSE (the
    // toolbar's own Cancel button, Escape via useCellClipboard, a
    // successful Merge/Clear/Change-price action) while a shift-range was
    // mid-flight, the next Shift+Arrow press must start a fresh range from
    // the current cell rather than resuming the old, now-stale anchor.
    useEffect(() => {
        if (!isSelectionMode) {
            shiftAnchorRef.current = null;
            shiftFocusRef.current = null;
        }
    }, [isSelectionMode]);
}